import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV_LINKS = [
  { to: "/#hear-aria", label: "Hear ARIA", testid: "nav-hear-aria" },
  { to: "/#how-it-works", label: "How It Works", testid: "nav-how-it-works" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
  { to: "/assessment", label: "Health Score", testid: "nav-health-score" },
  { to: "/find-a-pro", label: "Find a Pro", testid: "nav-find-a-pro" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const NavBar = () => {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");
  const [open, setOpen] = useState(false);

  // close on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // lock scroll when menu open on mobile
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const lastLink = isAdmin
    ? { to: "/", label: "Public Site", testid: "nav-home", gold: false }
    : { to: "/onboard-simple", label: "Start Free", testid: "nav-start-free", gold: true };

  return (
    <header
      data-testid="nav-bar"
      className="nn-nav"
      style={{
        background: "#0A1628",
        color: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 50,
        borderBottom: "3px solid #D4AF37",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Link
          to="/"
          data-testid="nav-logo"
          style={{ textDecoration: "none", display: "flex", alignItems: "center", flexShrink: 0 }}
          aria-label="NearNow home"
        >
          <Logo
            variant="light"
            size={40}
            testId="logo-placeholder"
            style={{ maxWidth: 160, objectFit: "contain" }}
          />
        </Link>

        {/* desktop nav */}
        <nav
          className="nn-nav-desktop"
          style={{
            display: "flex",
            gap: 20,
            alignItems: "center",
          }}
        >
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} style={navLink}>
              {l.label}
            </Link>
          ))}
          <Link
            to={lastLink.to}
            data-testid={lastLink.testid}
            style={{ ...navLink, color: lastLink.gold ? "#D4AF37" : "#fff" }}
          >
            {lastLink.label}
          </Link>
        </nav>

        {/* mobile hamburger */}
        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="nn-nav-toggle tap"
          style={{
            display: "none",
            background: "transparent",
            border: "1px solid #D4AF37",
            borderRadius: 8,
            color: "#D4AF37",
            padding: 10,
            cursor: "pointer",
            minWidth: 48,
            minHeight: 48,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* mobile dropdown */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="nn-nav-mobile-panel"
          style={{
            background: "#0A1628",
            borderTop: "1px solid #1a2640",
            padding: "10px 18px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              style={mobileLink}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to={lastLink.to}
            data-testid={`mobile-${lastLink.testid}`}
            style={{ ...mobileLink, color: lastLink.gold ? "#D4AF37" : "#fff" }}
          >
            {lastLink.label}
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 880px) {
          .nn-nav-desktop { display: none !important; }
          .nn-nav-toggle  { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
};

const navLink = {
  color: "#fff",
  textDecoration: "none",
  fontFamily: "Bebas Neue, sans-serif",
  fontSize: 18,
  letterSpacing: 1.5,
  padding: "10px 6px",
  whiteSpace: "nowrap",
};

const mobileLink = {
  color: "#fff",
  textDecoration: "none",
  fontFamily: "Bebas Neue, sans-serif",
  fontSize: 22,
  letterSpacing: 1.5,
  padding: "14px 6px",
  borderBottom: "1px solid #1a2640",
  minHeight: 56,
  display: "flex",
  alignItems: "center",
};

export default NavBar;
